import { User, Mail, Link2, Globe, Monitor, Smartphone, Maximize2, Clock, Languages, MapPin } from 'lucide-react'
import { MetadataField } from './MetadataField'
import { MetadataSection } from './MetadataSection'
import { BlurredField } from './BlurredField'
import { useTranslation } from '@/hooks/useTranslation'

interface VisitorMetadata {
	name?: string
	email?: string
	browser_name?: string
	browser_version?: string
	os_name?: string
	os_version?: string
	device_type?: 'desktop' | 'mobile' | 'tablet' | 'unknown'
	screen_width?: number
	screen_height?: number
	current_page_url?: string
	page_title?: string
	timezone?: string
	language?: string
	city?: string
	country_code?: string
	region?: string
	[key: string]: any
}

interface EssentialMetadataProps {
	metadata: VisitorMetadata
	isPro: boolean
	isRestricted?: boolean
	recentlyChanged?: string[]
	visitorName?: string
	visitorEmail?: string
	collapsible?: boolean
	hideProfile?: boolean
}

export function EssentialMetadata({
	metadata,
	isPro,
	isRestricted = false,
	recentlyChanged = [],
	visitorName,
	visitorEmail,
	collapsible = false,
	hideProfile = false,
}: EssentialMetadataProps) {
	const { t } = useTranslation()
	// Format browser string
	const browserDisplay = metadata.browser_name && metadata.browser_version
		? `${metadata.browser_name} ${metadata.browser_version}`
		: metadata.browser_name || null

	// Format OS string
	const osDisplay = metadata.os_name && metadata.os_version
		? `${metadata.os_name} ${metadata.os_version}`
		: metadata.os_name || null

	// Format device type
	const deviceDisplay = metadata.device_type
		? metadata.device_type.charAt(0).toUpperCase() + metadata.device_type.slice(1)
		: null

	// Get page title or extract from URL
	const getPageDisplay = () => {
		if (metadata.page_title) return metadata.page_title
		if (metadata.current_page_url) {
			try {
				const url = new URL(metadata.current_page_url)
				return url.pathname === '/' ? url.hostname : url.pathname
			} catch {
				return metadata.current_page_url
			}
		}
		return null
	}

	const pageDisplay = getPageDisplay()

	const screenDisplay = metadata.screen_width && metadata.screen_height
		? `${metadata.screen_width}×${metadata.screen_height}`
		: null

	const getLocationDisplay = () => {
		const parts = []
		if (metadata.city) parts.push(metadata.city)
		if (metadata.region) parts.push(metadata.region)
		if (metadata.country_code) parts.push(metadata.country_code)
		return parts.join(', ') || null
	}
	const locationDisplay = getLocationDisplay()

	// Check if we have any profile data
	const hasProfileData = !hideProfile && (visitorName || visitorEmail)
	// Check if we have any page data
	const hasPageData = metadata.current_page_url
	// Check if we have any technical data (browser, OS, device, screen, timezone, language, location)
	const hasTechnicalData = browserDisplay || osDisplay || deviceDisplay ||
		screenDisplay || metadata.timezone || metadata.language || locationDisplay

	if (isRestricted) {
		return (
			<div className="space-y-3" role="region" aria-label={t('essentialMetadata.essentialVisitorInformationRestricted')}>
				{hasProfileData && (
					<MetadataSection title={t('essentialMetadata.profile')} icon={User} collapsible={collapsible}>
						{visitorName && (
							<BlurredField label={t('essentialMetadata.name')} icon={User} />
						)}
						{visitorEmail && (
							<BlurredField label={t('essentialMetadata.email')} icon={Mail} />
						)}
					</MetadataSection>
				)}

				{hasPageData && pageDisplay && (
					<MetadataSection title={t('essentialMetadata.currentPage')} icon={Link2} collapsible={collapsible}>
						<BlurredField label={t('essentialMetadata.page')} icon={Link2} />
					</MetadataSection>
				)}

				{hasTechnicalData && (
					<MetadataSection title={t('essentialMetadata.technical')} icon={Monitor} collapsible={collapsible}>
						{browserDisplay && (
							<BlurredField label={t('essentialMetadata.browser')} icon={Globe} />
						)}
						{osDisplay && (
							<BlurredField label={t('essentialMetadata.os')} icon={Monitor} />
						)}
						{deviceDisplay && (
							<BlurredField label={t('essentialMetadata.device')} icon={Smartphone} />
						)}
						{screenDisplay && (
							<BlurredField label={t('expandableDetails.screen')} icon={Maximize2} />
						)}
						{metadata.timezone && (
							<BlurredField label={t('expandableDetails.timezone')} icon={Clock} />
						)}
						{metadata.language && (
							<BlurredField label={t('expandableDetails.language')} icon={Languages} />
						)}
						{locationDisplay && (
							<BlurredField label={t('expandableDetails.location')} icon={MapPin} />
						)}
					</MetadataSection>
				)}
			</div>
		)
	}

	return (
		<div className="space-y-3" role="region" aria-label={t('essentialMetadata.essentialVisitorInformation')}>
			{hasProfileData && (
				<MetadataSection title={t('essentialMetadata.profile')} icon={User} collapsible={collapsible}>
					{visitorName && (
						<MetadataField
							label={t('essentialMetadata.name')}
							value={visitorName}
							icon={User}
							changed={recentlyChanged.includes('visitor_name')}
						/>
					)}
					{visitorEmail && (
						<MetadataField
							label={t('essentialMetadata.email')}
							value={visitorEmail}
							icon={Mail}
							changed={recentlyChanged.includes('visitor_email')}
						/>
					)}
				</MetadataSection>
			)}

			{hasPageData && pageDisplay && (
				<MetadataSection title={t('essentialMetadata.currentPage')} icon={Link2} collapsible={collapsible}>
					<MetadataField
						label={t('essentialMetadata.page')}
						value={pageDisplay}
						href={metadata.current_page_url}
						icon={Link2}
						changed={recentlyChanged.includes('current_page_url') || recentlyChanged.includes('pageUrl')}
					/>
				</MetadataSection>
			)}

			{hasTechnicalData && (
				<MetadataSection title={t('essentialMetadata.technical')} icon={Monitor} collapsible={collapsible}>
					{browserDisplay && (
						<MetadataField
							label={t('essentialMetadata.browser')}
							value={browserDisplay}
							icon={Globe}
							changed={recentlyChanged.includes('browser_name') || recentlyChanged.includes('browser')}
						/>
					)}
					{osDisplay && (
						<MetadataField
							label={t('essentialMetadata.os')}
							value={osDisplay}
							icon={Monitor}
							changed={recentlyChanged.includes('os_name') || recentlyChanged.includes('os')}
						/>
					)}
					{deviceDisplay && (
						<MetadataField
							label={t('essentialMetadata.device')}
							value={deviceDisplay}
							icon={Smartphone}
							changed={recentlyChanged.includes('device_type') || recentlyChanged.includes('device')}
						/>
					)}
					{screenDisplay && (
						isRestricted ? (
							<BlurredField label={t('expandableDetails.screen')} icon={Maximize2} />
						) : (
							<MetadataField
								label={t('expandableDetails.screen')}
								value={screenDisplay}
								icon={Maximize2}
							/>
						)
					)}
					{metadata.timezone && (
						isRestricted ? (
							<BlurredField label={t('expandableDetails.timezone')} icon={Clock} />
						) : (
							<MetadataField
								label={t('expandableDetails.timezone')}
								value={metadata.timezone}
								icon={Clock}
							/>
						)
					)}
					{metadata.language && (
						isRestricted ? (
							<BlurredField label={t('expandableDetails.language')} icon={Languages} />
						) : (
							<MetadataField
								label={t('expandableDetails.language')}
								value={metadata.language}
								icon={Languages}
							/>
						)
					)}
					{locationDisplay && (
						isRestricted ? (
							<BlurredField label={t('expandableDetails.location')} icon={MapPin} />
						) : (
							<MetadataField
								label={t('expandableDetails.location')}
								value={locationDisplay}
								icon={MapPin}
							/>
						)
					)}
				</MetadataSection>
			)}

			{!hasProfileData && !hasPageData && !hasTechnicalData && (
				<div className="text-center py-6 px-4">
					<p className="text-sm text-muted-foreground">
						{t('essentialMetadata.noEssentialVisitorInformationAvailable')}
					</p>
				</div>
			)}
		</div>
	)
}
